import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Shield, Building2, AlertTriangle, Package,
  ChevronRight, TrendingUp, CheckCircle2, Clock, XCircle,
  Warehouse,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Skeleton } from "../components/ui/skeleton";
import { useAuth, API } from "../App";
import { toast } from "sonner";

// Lucide doesn't have FlaskConical / Leaf in all versions — use fallbacks
const FlaskIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 3h6M9 3v7l-4 9h14l-4-9V3"/>
  </svg>
);
const LeafIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z"/>
    <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"/>
  </svg>
);

const FRAMEWORK_META = {
  oea: {
    icon: Shield,
    color: "#f59e0b",
    bg: "bg-amber-50 dark:bg-amber-950/20",
    border: "border-amber-200 dark:border-amber-800",
    badge: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    path: "/zona-franca/oea",
    shortName: "OEA",
    subtitle: "Operador Económico Autorizado",
  },
  cnzfe: {
    icon: Building2,
    color: "#14b8a6",
    bg: "bg-teal-50 dark:bg-teal-950/20",
    border: "border-teal-200 dark:border-teal-800",
    badge: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400",
    path: "/zona-franca/cnzfe",
    shortName: "CNZFE",
    subtitle: "Reglamento 522-06 / Ley 8-90",
  },
  iso22000: {
    icon: LeafIcon,
    color: "#3b82f6",
    bg: "bg-blue-50 dark:bg-blue-950/20",
    border: "border-blue-200 dark:border-blue-800",
    badge: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    path: "/zona-franca/iso22000",
    shortName: "ISO 22000",
    subtitle: "Inocuidad Alimentaria",
  },
  bpm: {
    icon: FlaskIcon,
    color: "#8b5cf6",
    bg: "bg-purple-50 dark:bg-purple-950/20",
    border: "border-purple-200 dark:border-purple-800",
    badge: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
    path: "/zona-franca/bpm",
    shortName: "BPM",
    subtitle: "Buenas Prácticas de Manufactura",
  },
  haccp: {
    icon: AlertTriangle,
    color: "#ef4444",
    bg: "bg-red-50 dark:bg-red-950/20",
    border: "border-red-200 dark:border-red-800",
    badge: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
    path: "/zona-franca/haccp",
    shortName: "HACCP",
    subtitle: "Análisis de Peligros y CCPs",
  },
  nordom477: {
    icon: Package,
    color: "#06b6d4",
    bg: "bg-cyan-50 dark:bg-cyan-950/20",
    border: "border-cyan-200 dark:border-cyan-800",
    badge: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400",
    path: "/zona-franca/nordom477",
    shortName: "NORDOM 477",
    subtitle: "Norma Dominicana de Calidad",
  },
};

const getStatusIcon = (pct) => {
  if (pct >= 80) return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
  if (pct >= 40) return <Clock className="w-4 h-4 text-amber-500" />;
  return <XCircle className="w-4 h-4 text-red-500" />;
};

const getStatusLabel = (pct) => {
  if (pct >= 80) return "Conforme";
  if (pct >= 40) return "En Progreso";
  return "Requiere Atención";
};

const FrameworkCard = ({ fw }) => {
  const navigate = useNavigate();
  const meta = FRAMEWORK_META[fw.code] || {};
  const Icon = meta.icon || Shield;
  const completionPct = fw.completion_pct || 0;
  const compliancePct = fw.compliance_pct || 0;

  return (
    <Card
      className={`cursor-pointer hover:shadow-md transition-all border ${meta.border || ""} ${meta.bg || ""}`}
      onClick={() => navigate(meta.path || `/zona-franca/${fw.code}`)}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${meta.color}20`, border: `1px solid ${meta.color}40` }}
            >
              <Icon className="w-5 h-5" style={{ color: meta.color }} />
            </div>
            <div>
              <CardTitle className="text-base">{meta.shortName || fw.code.toUpperCase()}</CardTitle>
              <CardDescription className="text-xs">{meta.subtitle || fw.name}</CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {getStatusIcon(compliancePct)}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <div className="flex justify-between text-xs text-muted-foreground mb-1">
            <span>Evaluación completada</span>
            <span className="font-medium">{completionPct}%</span>
          </div>
          <Progress value={completionPct} className="h-1.5" />
        </div>
        <div>
          <div className="flex justify-between text-xs text-muted-foreground mb-1">
            <span>Controles conformes</span>
            <span className="font-medium text-foreground">{fw.compliant} / {fw.total_controls}</span>
          </div>
          <Progress
            value={compliancePct}
            className="h-1.5"
            style={{ "--progress-color": meta.color }}
          />
        </div>
        <div className="flex items-center justify-between pt-1">
          <Badge variant="outline" className={`text-[10px] ${meta.badge || ""}`}>
            {getStatusLabel(compliancePct)}
          </Badge>
          <Button variant="ghost" size="sm" className="h-6 text-xs gap-1 px-2">
            Ver módulo <ChevronRight className="w-3 h-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const ZonaFrancaDashboard = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/zona-franca/summary`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSummary(res.data);
      } catch {
        toast.error("No se pudo cargar el resumen de Zona Franca");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const frameworks = summary?.frameworks || [];
  const totalControls = frameworks.reduce((s, f) => s + (f.total_controls || 0), 0);
  const totalCompliant = frameworks.reduce((s, f) => s + (f.compliant || 0), 0);
  const avgCompletion = frameworks.length
    ? Math.round(frameworks.reduce((s, f) => s + (f.completion_pct || 0), 0) / frameworks.length)
    : 0;
  const overallCompliance = totalControls ? Math.round((totalCompliant / totalControls) * 100) : 0;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: "#f59e0b20", border: "1px solid #f59e0b40" }}>
            <Warehouse className="w-6 h-6" style={{ color: "#f59e0b" }} />
          </div>
          <div>
            <h1 className="text-2xl font-heading font-bold">Zona Franca</h1>
            <p className="text-muted-foreground text-sm">
              Cumplimiento integral para operadores de Zona Franca de la República Dominicana
            </p>
          </div>
        </div>
        <Badge
          className="text-xs px-3 py-1"
          style={{ backgroundColor: "#f59e0b20", color: "#d97706", border: "1px solid #f59e0b40" }}
        >
          Pro / Enterprise
        </Badge>
      </div>

      {/* KPI Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Módulos activos", value: frameworks.length, icon: Warehouse, color: "#f59e0b" },
          { label: "Total controles", value: totalControls, icon: Shield, color: "#10b981" },
          { label: "Conformes", value: totalCompliant, icon: CheckCircle2, color: "#22c55e" },
          { label: "Evaluación promedio", value: `${avgCompletion}%`, icon: TrendingUp, color: "#3b82f6" },
        ].map((kpi) => {
          const KpiIcon = kpi.icon;
          return (
            <Card key={kpi.label}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${kpi.color}20` }}>
                    <KpiIcon className="w-4 h-4" style={{ color: kpi.color }} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{loading ? "—" : kpi.value}</p>
                    <p className="text-xs text-muted-foreground">{kpi.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Overall compliance bar */}
      {!loading && totalControls > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Cumplimiento global Zona Franca</span>
              <span className="text-sm font-bold" style={{ color: "#f59e0b" }}>{overallCompliance}%</span>
            </div>
            <Progress value={overallCompliance} className="h-2" />
            <p className="text-xs text-muted-foreground mt-1.5">
              {totalCompliant} de {totalControls} controles conformes en todos los módulos
            </p>
          </CardContent>
        </Card>
      )}

      {/* Framework Grid */}
      <div>
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
          Sub-módulos de cumplimiento
        </h2>
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="p-4 space-y-3">
                  <Skeleton className="h-10 w-10 rounded-xl" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-2 w-full" />
                  <Skeleton className="h-2 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {frameworks.map((fw) => (
              <FrameworkCard key={fw.code} fw={fw} />
            ))}
          </div>
        )}
      </div>

      {/* Quick actions */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Acciones rápidas</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate("/zona-franca/oea")}>
              <Shield className="w-3.5 h-3.5 mr-1.5" /> Evaluación OEA
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/zona-franca/haccp")}>
              <AlertTriangle className="w-3.5 h-3.5 mr-1.5" /> Plan HACCP
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/zona-franca/bpm")}>
              <FlaskIcon className="w-3.5 h-3.5 mr-1.5" /> Listas BPM
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/zona-franca/cnzfe")}>
              <Building2 className="w-3.5 h-3.5 mr-1.5" /> CNZFE 522-06
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ZonaFrancaDashboard;
